import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {CustomerAdvancedSearchResultResponseBody} from '@dms/api/customer';

import {CustomerSelectSearchType} from '../types';
import {CustomerSearchRow} from './getCustomerSearchRows';

const toCustomerLevelRow = (
  customer: CustomerAdvancedSearchResultResponseBody
): CustomerSearchRow =>
  customer.discriminator === 'NATURAL_PERSON'
    ? {kind: 'naturalPerson', customer}
    : {kind: 'business', customer};

/**
 * For `type='customer'`: keep one customer-level row per customer, preserving order.
 * A customer matched only through a contact (so it appears solely as a `businessContact`
 * row) is still surfaced — synthesized as a single customer-level row — so it is never
 * hidden. Contact rows are otherwise dropped.
 */
const collapseToCustomerRows = (rows: CustomerSearchRow[]): CustomerSearchRow[] => {
  const byCustomerId = new Map<string, CustomerSearchRow>();

  rows.forEach((row) => {
    if (!isNil(byCustomerId.get(row.customer.id))) {
      return;
    }
    byCustomerId.set(
      row.customer.id,
      row.kind === 'businessContact' ? toCustomerLevelRow(row.customer) : row
    );
  });

  return Array.from(byCustomerId.values());
};

/**
 * Filters the flattened customer-search rows by the requested {@link CustomerSelectSearchType}.
 * Both the rendered list and the results-found count should be derived from the output.
 */
export const filterCustomerSearchRows = (
  rows: CustomerSearchRow[],
  type: CustomerSelectSearchType = 'all'
): CustomerSearchRow[] =>
  match(type)
    .with('all', () => rows)
    .with('contactPerson', () => rows.filter((row) => row.kind === 'businessContact'))
    .with('customer', () => collapseToCustomerRows(rows))
    .exhaustive();
